import React, { useState, useEffect } from 'react';
import { 
  Download,
  Users,
  TrendingUp,
  BarChart2,
  Activity,
  ArrowUp,
  ArrowDown,
  Loader
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { analyticsService } from '../services/adminServices';

// Constants
const DATE_RANGES = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  'custom': 'Custom Range'
};

const COLORS = {
  primary: '#3B82F6',
  success: '#10B981',
  indigo: '#6366F1',
  warning: '#F59E0B',
  error: '#EF4444'
};

function AnalyticsPage() {
  // State
  const [dateRange, setDateRange] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data fetch
  const fetchAnalytics = async () => {
    console.log('[AnalyticsPage - Fetch]', { dateRange });
  
    try {
      setLoading(true);
      console.log('[AnalyticsPage - Fetching analytics data]');
      const data = await analyticsService.getOverview(dateRange);
      console.log('[AnalyticsPage - Analytics data received]', data);
      setAnalytics(data);
    } catch (err) {
      console.error('[AnalyticsPage - Error]', err);
      setError('Failed to load analytics');
    } finally {
      console.log('[AnalyticsPage - Fetch completed]');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  useEffect(() => {
    if (analytics) {
      console.log('[AnalyticsPage - Analytics Data]', analytics);
    }
  }, [analytics]);

  // Helper functions
  const formatChange = (value) => {
    const number = parseFloat(value);
    return {
      value: `${Math.abs(number).toFixed(1)}%`,
      trend: number >= 0 ? 'up' : 'down'
    };
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(num);
  };

  // Render functions
  const renderHeader = () => (
    <div className="flex justify-between items-center">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Analytics</h1>
        <p className="mt-1 text-sm text-gray-500">Track platform performance and user engagement</p>
      </div>
      <div className="flex space-x-3">
        <select 
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
        >
          {Object.entries(DATE_RANGES).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2">
          <Download className="h-5 w-5" />
          <span>Export</span>
        </button>
      </div>
    </div>
  );

  const renderMetricCard = ({ title, value, change, trend, icon: Icon }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <h3 className="text-2xl font-bold text-gray-900 mt-2">{value}</h3>
        </div>
        <div className="p-2 bg-blue-50 rounded-lg">
          <Icon className="h-6 w-6 text-blue-600" />
        </div>
      </div>
      <div className="mt-4 flex items-center text-sm">
        {trend === 'up' ? (
          <ArrowUp className="h-4 w-4 text-green-500" />
        ) : (
          <ArrowDown className="h-4 w-4 text-red-500" />
        )}
        <span className={`font-medium ${
          trend === 'up' ? 'text-green-500' : 'text-red-500'
        }`}>
          {change}
        </span>
        <span className="text-gray-600 ml-2">vs last period</span>
      </div>
    </div>
  );

  const renderChart = ({ title, type, data, config, height = 80 }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      <div className={`h-${height}`}>
        <ResponsiveContainer width="100%" height="100%">
          {type === 'line' && (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={config.xAxis} />
              <YAxis />
              <Tooltip />
              {config.lines.map((line, index) => (
                <Line 
                  key={index} 
                  type="monotone" 
                  dataKey={line.key} 
                  stroke={line.color} 
                  name={line.name} />
              ))}
            </LineChart>
          )}
          {type === 'bar' && (
            <BarChart data={data} layout={config.layout}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type={config.layout === 'vertical' ? 'number' : 'category'} />
              <YAxis dataKey={config.layout === 'vertical' ? config.yAxis : undefined} type={config.layout === 'vertical' ? 'category' : 'number'} />
              <Tooltip />
              {config.bars.map((bar, index) => (
                <Bar
                  key={index}
                  dataKey={bar.key}
                  fill={bar.color}
                  name={bar.name}
                />
              ))}
            </BarChart>
          )}
          {type === 'pie' && (
            <PieChart>
              <Pie
                data={data}
                dataKey={config.dataKey}
                nameKey={config.nameKey}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                label
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  const metrics = analytics ? [
    {
      title: 'Total Users',
      value: formatNumber(analytics.users.total),
      ...formatChange(analytics.users.activeGrowth),
      icon: Users
    },
    {
      title: 'Active This Week',
      value: formatNumber(analytics.users.active),
      ...formatChange(analytics.users.active),
      icon: Activity
    },
    {
      title: 'New GoGets Today',
      value: formatNumber(analytics.content.newGoGets),
      ...formatChange(analytics.content.newGoGets),
      icon: TrendingUp
    },
    {
      title: 'Engagement Rate',
      value: `${analytics.users.engagementRate}%`,
      ...formatChange(analytics.users.engagementGrowth),
      icon: BarChart2
    }
  ] : [];

  return (
    <div className="space-y-6">
      {renderHeader()}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {metrics.map((metric, index) => renderMetricCard(metric))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {analytics && (
          <>
            {renderChart({
              title: 'User Growth',
              type: 'line',
              data: analytics.charts.userGrowth,
              config: {
                xAxis: 'date',
                lines: [
                  { key: 'total', name: 'Total Users', color: COLORS.primary },
                  { key: 'active', name: 'Active Users', color: COLORS.success },
                  { key: 'new', name: 'New Users', color: COLORS.indigo }
                ]
              }
            })}

            {renderChart({
              title: 'Engagement Metrics',
              type: 'bar',
              data: analytics.charts.engagement,
              config: {
                xAxis: 'date',
                bars: [
                  { key: 'wants', name: 'Wants', color: COLORS.primary },
                  { key: 'needs', name: 'Needs', color: COLORS.success },
                  { key: 'hads', name: 'Hads', color: COLORS.indigo }
                ]
              }
            })}

            {renderChart({
              title: 'User Distribution',
              type: 'pie',
              data: analytics.charts.userDistribution,
              config: {
                dataKey: 'value',
                nameKey: 'name'
              }
            })}

            {renderChart({
              title: 'Content Distribution',
              type: 'bar',
              data: analytics.charts.contentDistribution,
              config: {
                layout: 'vertical',
                yAxis: 'type',
                bars: [
                  { key: 'count', name: 'Count', color: COLORS.primary }
                ]
              }
            })}
          </>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPage;